import {
  CandidateSummaryGap,
  CandidateSummaryGapStatus,
  CandidateSummaryPayload,
} from '../models/candidate-summary.model';

function createGapId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `gap-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function normalizeAssessmentGaps(
  payload: CandidateSummaryPayload
): CandidateSummaryGap[] {
  if (payload.gaps?.length) {
    return payload.gaps.map((gap) => ({
      id: gap.id || createGapId(),
      description: gap.description ?? '',
      status: gap.status ?? 'OPEN',
      resolutionNote: gap.resolutionNote,
    }));
  }
  return (payload.gapsOrRisks ?? [])
    .map((description) => description.trim())
    .filter(Boolean)
    .map((description) => ({
      id: createGapId(),
      description,
      status: 'OPEN' as CandidateSummaryGapStatus,
    }));
}

export function countOpenGaps(payload?: CandidateSummaryPayload): number {
  if (!payload) {
    return 0;
  }
  return normalizeAssessmentGaps(payload).filter((gap) => gap.status === 'OPEN')
    .length;
}

export function hasOpenGaps(payload?: CandidateSummaryPayload): boolean {
  return countOpenGaps(payload) > 0;
}

export function gapsForClientExport(
  payload: CandidateSummaryPayload
): string[] {
  return normalizeAssessmentGaps(payload)
    .filter((gap) => gap.status === 'RESOLVED')
    .map((gap) =>
      gap.resolutionNote?.trim()
        ? `${gap.description.trim()} — Addressed: ${gap.resolutionNote.trim()}`
        : gap.description.trim()
    );
}

export function createEmptyGap(): CandidateSummaryGap {
  return {
    id: createGapId(),
    description: '',
    status: 'OPEN',
  };
}

export function validateGapsForSave(
  gaps: CandidateSummaryGap[]
): string | null {
  for (const gap of gaps) {
    if (!gap.description.trim()) {
      return 'Each gap needs a description.';
    }
    if (gap.status === 'RESOLVED' && !gap.resolutionNote?.trim()) {
      return 'Resolved gaps need a resolution note explaining how they were addressed.';
    }
  }
  return null;
}

export function withNormalizedGaps(
  payload: CandidateSummaryPayload
): CandidateSummaryPayload {
  return {
    ...payload,
    gaps: normalizeAssessmentGaps(payload).map((gap) => ({
      ...gap,
      description: gap.description.trim(),
      resolutionNote: gap.resolutionNote?.trim() || undefined,
    })),
    gapsOrRisks: undefined,
  };
}
